import axios from 'axios'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useAuthenticatedAxios } from '../axiosInstance'

const axiosClient = axios.create()

axiosClient.defaults.headers.common = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
	'Access-Control-Allow-Origin': '*',
	'Access-Control-Allow-Credentials': true,
	'Access-Control-Allow-Headers': 'X-Requested-With,content-type'
}

export type TickerItem = {
	ticker: string
	herd: number
	decoder: number
	news: number
	company: string
	sector: string
	lo1: string
}

type PresignedUrlResponse = {
	url: string
}

export const fetchHerdPowerTickers = async (accessToken: string | undefined): Promise<TickerItem[]> => {
	try {
		// Step 1: Get the pre-signed URL from our backend
		const presignedUrlResponse = await axiosClient.get<PresignedUrlResponse>(
			`${process.env.REACT_APP_API_SERVER}/storage/search/ticker-list`,
			{
				headers: {
					Authorization: `Bearer ${accessToken}`
				}
			}
		)

		const { url: presignedUrl } = presignedUrlResponse.data

		// Step 2: Fetch the JSON file from S3 using the pre-signed URL
		const s3Response = await axiosClient.get<TickerItem[]>(presignedUrl, {
			// Don't send auth headers to S3
			headers: {
				Authorization: undefined
			}
		})

		return s3Response.data
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 403) {
				throw new Error('Access denied to S3 resource')
			}
			if (error.code === 'CORS_ERROR') {
				throw new Error('CORS error while accessing S3 resource')
			}
			throw new Error(`Failed to fetch tickers: ${error.message}`)
		}
		throw new Error('An unexpected error occurred while fetching tickers')
	}
}

// Updated hook that accepts options and doesn't require passing the access token manually
export const useTickers = (options?: Partial<UseQueryOptions<TickerItem[], Error>>) => {
	const authenticatedAxios = useAuthenticatedAxios()

	return useQuery<TickerItem[], Error>({
		queryKey: ['ticker-list.json'],
		queryFn: async () => {
			try {
				// Step 1: Get the pre-signed URL
				const presignedUrlResponse = await authenticatedAxios.get<PresignedUrlResponse>(
					`${process.env.REACT_APP_API_SERVER}/storage/search/ticker-list`
				)

				const { url: presignedUrl } = presignedUrlResponse.data

				// Step 2: Fetch from S3
				const s3Response = await axiosClient.get<TickerItem[]>(presignedUrl, {
					headers: {
						Authorization: undefined
					}
				})

				return s3Response.data
			} catch (error) {
				if (axios.isAxiosError(error)) {
					if (error.response?.status === 403) {
						throw new Error('Access denied to S3 resource')
					}
					if (error.code === 'CORS_ERROR') {
						throw new Error('CORS error while accessing S3 resource')
					}
					throw new Error(`Failed to fetch tickers: ${error.message}`)
				}
				throw new Error('An unexpected error occurred while fetching tickers')
			}
		},
		staleTime: Infinity, // Data never becomes stale
		gcTime: Infinity, // Keep data in cache forever
		refetchOnMount: false, // Don't refetch on mount if data exists
		refetchOnWindowFocus: false, // Don't refetch on window focus
		refetchOnReconnect: false, // Don't refetch on reconnect
		...options
	})
}
